<template>
  <div class="mt-4">
    <v-row>
      <v-col md="3">
        <competence-sidebar
          :active-competence-id="compId"
          route-name="competence_achievements_sync"
        ></competence-sidebar>
      </v-col>
      <v-col md="9">
        <div class="mb-4">
          <h3>Задачи на создание достижений</h3>
          <p>
            Здесь отображаются задачи создания достижений по конкурсу талант для
            компетенции.
            <strong>{{ competence ? competence.name : compId }}</strong
            >.
          </p>

          <v-expansion-panels class="mb-4">
            <v-expansion-panel>
              <v-expansion-panel-header
                ><h3>Как это работает?</h3></v-expansion-panel-header
              >
              <v-expansion-panel-content>
                <p>
                  Задача по выдаче достижений обрабатывает записи в таблице
                  userScore по выбранной компетенции. Вычисляет роль достижения
                  на основе текущих баллов. После чего создает/обновляет/удаляет
                  достижения у пользователя за главное мероприятие компетенции.
                </p>

                <ol class="mb-2">
                  <li>
                    Если пользователь набрал баллы достаточные для выдачи
                    достижения:
                    <ol>
                      <li>
                        Если существует ранее выданное достижение, то оно
                        обновляется новыми баллами и ролью.
                      </li>
                      <li>
                        Если не существует – то создается новое достижение с
                        указаной ролью и баллами.
                      </li>
                    </ol>
                  </li>
                  <li>
                    Если набранных баллов не достаточно для выдачи достижения,
                    то ранее выданное достижение удаляется.
                  </li>
                </ol>

                <p>
                  Таким образом, после выполнения задачи по выдачи достижений,
                  все нерелевантные достижения будут удалены, а новые будут
                  созданы или обновлены.
                </p>

                <p>
                  Задача по выдаче достижений,
                  <strong>не выполняет</strong> пересчет баллов пользователя по
                  компетенции. Она работает с уже посчитанными баллами. Если вам
                  нужно выдать достижения на основе новых баллов, то сначала
                  выполните задачу по
                  <router-link :to="{ name: 'competence_sync' }"
                    >пересчету баллов</router-link
                  >.
                </p>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div class="my-2">
            Выдано достижений за компетенцию:
            <a :href="achivementsLink" :target="`_achievements_${compId}`">{{
              achievementsCount
            }}</a>
            из
            {{ totalUsersWithPossibleAchievement }}
          </div>

          <v-btn color="primary" :loading="createPending" @click="createTask"
            >Создать достижения</v-btn
          >
        </div>
        <div class="my-3">
          Найдено задач: {{ total }}.
          <a href="#" @click.prevent="getList(1)">Обновить список</a>
        </div>
        <div style="min-height: 4px">
          <v-progress-linear
            indeterminate
            color="yellow darken-2"
            :active="pending"
          ></v-progress-linear>
        </div>
        <v-simple-table class="mb-3">
          <template #default>
            <thead>
              <tr>
                <th class="text-left">№</th>
                <th class="text-left">Статус</th>
                <th class="text-left">Дата создания</th>
                <th class="text-left">Автор</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="task in taskList" :key="task.id">
                <td>
                  {{ task.id }}
                </td>
                <td>
                  <v-icon v-if="task.statusIcon" color="grey lighten-1" left>{{
                    task.statusIcon
                  }}</v-icon
                  >{{ task.statusText || task.status }}
                </td>
                <td>{{ task.created_at }}</td>
                <td>{{ task.author }}</td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
        <app-pager
          :value="page"
          :total="total"
          :limit="limit"
          class="pager"
          @input="getList"
        ></app-pager>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { apiClient } from "@/api";
import { getStatusData } from "@/services/task";
import dayjs from "@/plugins/dayjs";
import CompetenceSidebar from "@/components/CompetenceSidebar";
import { TALENT_API_V2_URL } from "@/constants";

const ACHIEVEMENT_PROGRESS_INTERVAL = 1000 * 60 * 3;

export default {
  name: "AchievementSync",
  components: {
    CompetenceSidebar,
  },
  props: {
    thresholds: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  data() {
    return {
      pending: false,
      page: 1,
      limit: 10,
      total: 0,
      error: "",
      tasks: [],
      createPending: false,
      achivementProgressInterval: null,
      scoresDistribution: [],
      achievementsCount: 0,
      lastAchievementCheck: 0,
    };
  },
  computed: {
    user() {
      return this.$store.getters["user/user"];
    },
    compId() {
      return +this.$route.params.id;
    },
    achivementsLink() {
      if (!this.competence?.event_id) {
        return "#";
      }
      return `${process.env.VUE_APP_TALENT_HOST}/admin/package/achievement/?event__pk__exact=${this.competence?.event_id}`;
    },
    competence() {
      return this.$store.getters["competenceById"](this.compId);
    },
    /**
     * Минимальный балл чтобы получить ачивку
     */
    minScore() {
      if (!this.thresholds?.length) {
        return 0;
      }
      return Math.min(...this.thresholds.map((t) => t.lower_bound));
    },
    taskList() {
      return this.tasks.map((task) => {
        const status = getStatusData(task.status);
        let author;
        if (task.author_id === this.user?.id) {
          author = "Вы";
        } else {
          author = task.author_id;
        }
        return {
          ...task,
          ...(status && { ...status }),
          created_at: dayjs(task.created_at).format("LLL"),
          author,
        };
      });
    },
    totalUsersWithPossibleAchievement() {
      const { minScore, scoresDistribution } = this;
      if (!minScore || !scoresDistribution?.length) {
        return 0;
      }
      return scoresDistribution.reduce((acc, item) => {
        if (item.score >= this.minScore) {
          return acc + item.count;
        }
        return acc;
      }, 0);
    },
  },
  watch: {
    compId() {
      clearTimeout(this._polingInterval);
      this.getList(1);
      this.achievementsCount = 0;
      this.scoresDistribution = [];
      this.fetchProgress();
    },
  },
  created() {
    this._polingInterval = null;
    this._watchTask = null;
    this.getList();
    this.fetchProgress();
  },
  beforeDestroy() {
    clearTimeout(this._polingInterval);
    clearTimeout(this.achivementProgressInterval);
  },
  methods: {
    async getList(page = 1) {
      if (this.pending) return;
      this.pending = true;
      this.error = "";
      try {
        const { data } = await apiClient({
          url: `/competences/${this.compId}/achievements_tasks`,
          params: {
            limit: this.limit,
            offset: this.limit * (page - 1),
          },
        });
        this.tasks = data.results;
        if ("count" in data) {
          this.total = data.count;
        }
        this.page = page;
      } catch (error) {
        this.error = error?.message || "Непредвиденная ошибка";
      }
      this.pending = false;
    },
    async createTask() {
      const confirm = await this.$root.$confirm(
        "Пересчитать баллы",
        `Вы уверены, что хотите создать задачу на достижения за компетенцию ${this.competence?.name}?`,
        {
          confirmText: "Да, создать достижения",
          rejectText: "Нет, отмена",
          confirmColor: "primary",
        }
      );
      if (!confirm) return;
      this.createPending = true;
      try {
        const { data } = await apiClient({
          method: "POST",
          url: `/competences/${this.compId}/achievements_tasks/`,
        });
        this.$toast(`Заддача на достижения №${data.id} добавлена в очередь`, {
          type: "success",
        });

        if (this.page === 1 && this.total < this.limit) {
          this.tasks.unshift(data);
          if (this.total >= this.limit) {
            this.tasks.splice(this.limit - 1, 1);
          }
          this.total += 1;
        } else {
          this.getList(1);
        }
      } catch (error) {
        this.$toast(`Не удалось создать задачу. ${error.message}`, {
          type: "error",
        });
      }
      this.createPending = false;
    },
    async getScoresDistribution() {
      const { data } = await apiClient({
        method: "GET",
        url: "/scores/distribution",
        params: {
          competences_ids: this.compId,
          years: new Date().getFullYear(),
          hide_zeros: true,
        },
      });
      this.scoresDistribution = data;
    },

    async getAchievementsCount() {
      if (!this.competence?.event_id) {
        return 0;
      }
      const { data } = await apiClient({
        method: "GET",
        url: `/events/${this.competence.event_id}`,
        baseURL: TALENT_API_V2_URL,
        params: {
          extend: true,
        },
      });
      this.achievementsCount = data.achievements_count || 0;
    },

    async getAchievementsProgress() {
      await Promise.all([
        this.getAchievementsCount(),
        this.getScoresDistribution(),
      ]);
      this.lastAchievementCheck = Date.now();
    },

    async fetchProgress() {
      clearTimeout(this.achivementProgressInterval);
      try {
        await this.getAchievementsProgress();
        this.achivementProgressInterval = setTimeout(
          this.fetchProgress,
          ACHIEVEMENT_PROGRESS_INTERVAL
        );
      } catch (error) {
        console.error("Error fetching achievements progress:", error);
        this.$toast(
          `Не удалось получить информацию по прогрессу достижений. ${error.message}`,
          {
            type: "error",
          }
        );
        clearTimeout(this.achivementProgressInterval);
      }
    },
  },
};
</script>

<style scoped>
.pager >>> .v-pagination {
  justify-content: flex-start !important;
}
</style>
